import {
  AddBuyableItems,
  useAddItemsMutation,
  UseAddItemsMutationOptions,
  useGetUserBasket,
} from "@ac-expo/api";
import { BuyableItemCreationType, GetUserBasket } from "@ac-expo/api-types";
import { getBuyableItemGroupKey } from "@ac-expo/utils";
import { BasketType } from "@prisma/client";
import { MutateOptions, MutationFunctionContext } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getGuestCartItems,
  guestCartEmitter,
  GuestCartStorageItem,
  mergeGuestCartItems,
} from "./guest-cart-storage";
import { BuyableItemWithPricing, useLocalCart } from "./useLocalCart";

const extractBaseQuantities = (
  cart: GetUserBasket,
  availableBuyableItemsKeys: string[],
): Record<string, number> => {
  if (!cart.basketItem) return {};

  const quantityMap: Record<string, number> = {};

  cart.basketItem.forEach((basketItem) => {
    const key = getBuyableItemGroupKey(basketItem.buyableItem);
    if (availableBuyableItemsKeys.includes(key)) {
      quantityMap[key] = (quantityMap[key] || 0) + 1;
    }
  });

  return quantityMap;
};

export const useHydratedLocalCart = ({
  basketType,
  availableBuyableItems,
  isAuthenticated,
  onAddToCartMutationSuccess,
  onAddToCartMutationError,
}: {
  basketType: BasketType;
  availableBuyableItems: BuyableItemWithPricing[];
  isAuthenticated: boolean;
  onAddToCartMutationSuccess?: UseAddItemsMutationOptions["onSuccess"];
  onAddToCartMutationError?: UseAddItemsMutationOptions["onError"];
}) => {
  const { data: cart, isLoading } = useGetUserBasket(basketType);
  const { mutate, isPending } = useAddItemsMutation(basketType, {
    onSuccess: onAddToCartMutationSuccess,
    onError: onAddToCartMutationError,
  });

  const [guestCartItems, setGuestCartItemsState] = useState<GuestCartStorageItem[]>(() =>
    isAuthenticated ? [] : getGuestCartItems(basketType),
  );

  useEffect(() => {
    if (isAuthenticated) {
      setGuestCartItemsState([]);
      return;
    }

    setGuestCartItemsState(getGuestCartItems(basketType));

    const handleGuestCartChange = () => {
      setGuestCartItemsState(getGuestCartItems(basketType));
    };

    guestCartEmitter.on("change", handleGuestCartChange);
    return () => {
      guestCartEmitter.off("change", handleGuestCartChange);
    };
  }, [basketType, isAuthenticated]);

  const baseQuantities = useMemo(() => {
    const availableBuyableItemsKeys = availableBuyableItems.map((item) =>
      getBuyableItemGroupKey(item.buyableItem),
    );

    if (!cart) return {};
    return extractBaseQuantities(cart, availableBuyableItemsKeys);
  }, [cart, availableBuyableItems]);

  const guestBaseQuantities = useMemo(() => {
    if (isAuthenticated || guestCartItems.length === 0) return {};

    const availableBuyableItemsKeys = new Set(
      availableBuyableItems.map((item) => getBuyableItemGroupKey(item.buyableItem)),
    );
    const quantityMap: Record<string, number> = {};

    guestCartItems.forEach((item) => {
      if (!availableBuyableItemsKeys.has(item.key)) return;
      quantityMap[item.key] = (quantityMap[item.key] || 0) + item.quantity;
    });

    return quantityMap;
  }, [availableBuyableItems, guestCartItems, isAuthenticated]);

  const localCart = useLocalCart(
    availableBuyableItems,
    isAuthenticated ? baseQuantities : guestBaseQuantities,
  );
  const { localCartItems } = localCart;

  const mutateCart = useCallback(
    (options?: MutateOptions<unknown, Error, AddBuyableItems, unknown>) => {
      const filteredCartItems = localCartItems.filter((item) => item.quantity > 0);
      if (filteredCartItems.length === 0) {
        const variables = { items: [] };
        const mockContext = {} as MutationFunctionContext;
        options?.onSuccess?.(undefined, variables, undefined, mockContext);
        onAddToCartMutationSuccess?.(undefined, variables, undefined, mockContext);
        return;
      }

      const items = filteredCartItems.map((item) => ({
        buyableItem: item.buyableItem as BuyableItemCreationType,
        quantity: item.quantity,
      }));

      if (!isAuthenticated) {
        mergeGuestCartItems(
          basketType,
          filteredCartItems.map((item) => ({
            key: item.key,
            buyableItem: item.buyableItem,
            pricing: item.pricing,
            quantity: item.quantity,
          })),
        );
        const variables = { items };
        const mockContext = {} as MutationFunctionContext;
        options?.onSuccess?.(undefined, variables, undefined, mockContext);
        onAddToCartMutationSuccess?.(undefined, variables, undefined, mockContext);
        return;
      }

      mutate({ items }, options);
    },
    [basketType, isAuthenticated, localCartItems, mutate, onAddToCartMutationSuccess],
  );

  return {
    localCart,
    mutateCart,
    isPending,
    isLoading,
  };
};
